import React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Button - Komponent przycisku w stylu Radix Themes
 * 
 * API zgodne z @radix-ui/themes
 * - Rozmiary: "1" | "2" | "3" | "4"
 * - Warianty: "classic" | "solid" | "soft" | "surface" | "outline" | "ghost"
 * - Color: "pink" (accent) - domyślny
 * 
 * @example
 * ```tsx
 * <Button variant="solid" size="2">
 *   Zapisz
 * </Button>
 * <Button variant="soft" size="3" color="pink">
 *   Anuluj
 * </Button>
 * ```
 */

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-[var(--space-2)]",
    "whitespace-nowrap",
    "font-[var(--font-weight-medium)]",
    "transition-all duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-[var(--pink-9)] focus-visible:ring-offset-3", // WCAG AAA enhanced focus
    "disabled:pointer-events-none disabled:opacity-50",
    "active:scale-[0.98]",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
  ),
  {
    variants: {
      variant: {
        // Classic - soft glass with subtle border
        classic: cn(
          "bg-[rgba(17,17,17,0.3)] text-[var(--text-white-95)]",
          "backdrop-blur-[10px] backdrop-saturate-[150%]",
          "border border-[rgba(255,255,255,0.15)]",
          "hover:bg-[rgba(233,61,130,0.15)] hover:border-[rgba(233,61,130,0.3)]",
          "shadow-[0_2px_8px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.05)]",
          "hover:shadow-[0_4px_12px_rgba(233,61,130,0.15),inset_0_1px_0_rgba(255,255,255,0.08)]"
        ),
        // Solid - soft glassmorphism with pink accent
        solid: cn(
          "bg-[rgba(233,61,130,0.5)] text-white",
          "backdrop-blur-[16px] backdrop-saturate-[180%]",
          "border border-[rgba(233,61,130,0.5)]",
          "hover:bg-[rgba(233,61,130,0.65)]",
          "shadow-[0_4px_20px_rgba(233,61,130,0.3),0_0_40px_rgba(233,61,130,0.15),inset_0_1px_0_rgba(255,255,255,0.15)]",
          "hover:shadow-[0_8px_30px_rgba(233,61,130,0.4),0_0_60px_rgba(233,61,130,0.25),inset_0_1px_0_rgba(255,255,255,0.2)]"
        ),
        // Soft - bardzo subtelne kolorowe tło
        soft: cn(
          "bg-[var(--pink-3)] text-[var(--pink-11)]",
          "border border-transparent",
          "hover:bg-[var(--pink-4)]",
          "shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
        ),
        // Surface - prawie transparentne z subtlnym borderem
        surface: cn(
          "bg-[var(--dark-a2)] text-[var(--text-white-95)]",
          "border border-[var(--dark-a5)]",
          "hover:bg-[var(--dark-a3)] hover:border-[var(--pink-a5)]",
          "shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
        ),
        // Outline - glass outline
        outline: cn(
          "bg-[rgba(17,17,17,0.2)] text-[var(--text-white-95)]",
          "backdrop-blur-[8px]",
          "border border-[rgba(255,255,255,0.2)]",
          "hover:bg-[rgba(233,61,130,0.1)] hover:border-[rgba(233,61,130,0.4)]",
          "hover:text-[var(--pink-11)]",
          "shadow-[0_1px_4px_rgba(0,0,0,0.1)]"
        ),
        // Ghost - całkowicie transparentne
        ghost: cn(
          "bg-transparent text-[var(--text-white-95)]",
          "border border-transparent",
          "hover:bg-[var(--dark-a3)]",
          "hover:text-[var(--pink-11)]"
        ),
      },
      size: {
        "1": cn(
          "h-[24px] px-[var(--space-3)]",
          "text-[var(--font-size-1)]",
          "rounded-[var(--radius-sm)]"
        ),
        "2": cn(
          "h-[32px] px-[var(--space-4)]",
          "text-[var(--font-size-2)]",
          "rounded-[var(--radius-base)]"
        ),
        "3": cn(
          "h-[40px] px-[var(--space-7)]",
          "text-[var(--font-size-3)]",
          "rounded-[var(--radius-md)]"
        ),
        "4": cn(
          "h-[48px] px-[var(--space-6)]",
          "text-[var(--font-size-4)]",
          "rounded-[var(--radius-lg)]"
        ),
      },
      // Full width
      fullWidth: {
        true: "w-full",
        false: "",
      },
      // Loading state
      loading: {
        true: "cursor-wait opacity-70",
        false: "",
      },
    },
    defaultVariants: {
      variant: "solid",
      size: "2",
      fullWidth: false,
      loading: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    fullWidth,
    loading,
    asChild = false, 
    disabled,
    children,
    ...props 
  }, ref) => {
    const Comp = asChild ? Slot : "button"
    const combinedClassName = cn(buttonVariants({ variant, size, fullWidth, loading, className }))
    
    if (asChild) {
      return (
        <Slot
          className={combinedClassName}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      )
    }
    
    return (
      <button
        className={combinedClassName}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <>
            <svg 
              className="animate-spin -ml-1 mr-2 h-4 w-4" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24"
              aria-hidden="true"
              role="img"
              aria-label="Ładowanie"
            >
              <circle 
                className="opacity-25" 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
              />
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="sr-only">Ładowanie...</span>
          </>
        )}
        {children}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
